import { inject } from '@angular/core';
import { CanActivateFn, Routes, mapToCanActivate, mapToCanDeactivate } from '@angular/router';
import { BrandingService } from '@next/core-lib/branding';
import { LocationGuard } from '@next/core-lib/relationships';

import { AnimalGroupComponent } from './components/animal-group/animal-group.component';
import { BalanceComponent } from './components/balance/balance.component';
import { ClosureComponent } from './components/closure/closure.component';
import { CycleAddComponent } from './components/cycle-add/cycle-add.component';
import { CycleItemComponent } from './components/cycle-item/cycle-item.component';
import { CycleOverviewComponent } from './components/cycle-overview/cycle-overview.component';
import { DayOldChickComponent } from './components/day-old-chick/day-old-chick.component';
import { DayOldChickItemComponent } from './components/day-old-chick-item/day-old-chick-item.component';
import { DepartureComponent } from './components/departure/departure.component';
import { DepartureItemComponent } from './components/departure-item/departure-item.component';
import { DiscountOverviewComponent } from './components/discount-overview/discount-overview.component';
import { CarbonFootprintComponent } from './components/emission/carbon-footprint/carbon-footprint.component';
import { FarmResourcesComponent } from './components/emission/farm-resources/farm-resources.component';
import { FinalSetupComponent } from './components/final-setup/final-setup.component';
import { GeneralComponent } from './components/general/general.component';
import { CycleComponent } from './cycle.component';
import { CyclesGuard } from './guards/cycles.guard';
import { DiscountNavigationGuard } from './guards/discount-navigation.guard';
import { FinalSetupNavigationGuard } from './guards/final-setup-navigation.guard';
import { ObservationsGuard } from './guards/observations.guard';
import { PendingBalanceGuard } from './guards/pending-balance.guard';

const carbonFootprintTenantGuard: CanActivateFn = () =>
  !inject(BrandingService).isTenant('Agravis');

const cycleFeatureRoutes: Routes = [
  {
    path: '',
    component: CycleOverviewComponent,
    canActivate: mapToCanActivate([LocationGuard, CyclesGuard]),
    data: { name: 'cycle-overview' },
  },
  {
    path: 'add',
    component: CycleAddComponent,
    canActivate: mapToCanActivate([LocationGuard, CyclesGuard]),
    children: [
      {
        path: '',
        component: GeneralComponent,
        canActivate: mapToCanActivate([LocationGuard, CyclesGuard]),
        data: { name: 'general' },
      },
    ],
  },
  {
    path: ':cycleId',
    component: CycleItemComponent,
    canActivate: mapToCanActivate([LocationGuard]),
    children: [
      {
        path: '',
        component: GeneralComponent,
        canActivate: mapToCanActivate([LocationGuard, ObservationsGuard]),
        data: { name: 'general' },
      },
      {
        path: 'day-old-chick',
        component: DayOldChickComponent,
        canActivate: mapToCanActivate([LocationGuard]),
        data: { name: 'day-old-chick' },
      },
      {
        path: 'departure',
        component: DepartureComponent,
        canActivate: mapToCanActivate([LocationGuard]),
        data: { name: 'departure' },
      },
      {
        path: 'balance',
        component: BalanceComponent,
        canActivate: mapToCanActivate([LocationGuard]),
        canDeactivate: mapToCanDeactivate([PendingBalanceGuard]),
        data: { name: 'balance' },
      },
      {
        path: 'discount',
        component: DiscountOverviewComponent,
        canActivate: mapToCanActivate([LocationGuard, DiscountNavigationGuard]),
        data: { name: 'discount' },
      },
      {
        path: 'closure',
        component: ClosureComponent,
        canActivate: mapToCanActivate([LocationGuard]),
        data: { name: 'closure' },
      },
      {
        path: 'animal-group',
        component: AnimalGroupComponent,
        canActivate: mapToCanActivate([LocationGuard]),
        data: { name: 'animal-group' },
      },
      {
        path: 'final-setup',
        component: FinalSetupComponent,
        canActivate: mapToCanActivate([LocationGuard, FinalSetupNavigationGuard]),
        data: { name: 'final-setup' },
      },
    ],
  },
  {
    path: ':cycleId/day-old-chick/:dayOldChickId',
    component: DayOldChickItemComponent,
    canActivate: mapToCanActivate([LocationGuard]),
    data: { name: 'day-old-chick-item' },
  },
  {
    path: ':cycleId/departure/:departureId',
    component: DepartureItemComponent,
    canActivate: mapToCanActivate([LocationGuard]),
    data: { name: 'departure-item' },
  },
];

export const cycleRoutes: Routes = [
  {
    path: 'cycle',
    component: CycleComponent,
    children: cycleFeatureRoutes,
    canActivate: mapToCanActivate([LocationGuard]),
  },
  {
    path: 'farm-resources',
    component: FarmResourcesComponent,
    canActivate: mapToCanActivate([LocationGuard]),
  },
  {
    path: 'carbon-footprint',
    component: CarbonFootprintComponent,
    canActivate: [carbonFootprintTenantGuard, ...mapToCanActivate([LocationGuard])],
  },
];
